import * as React from 'react';
import {connect} from 'react-redux';

import {Field, formValueSelector, reset, change} from 'redux-form';

import {Label} from 'shells/form/label';
import {nonNull} from 'shells/form/validation/non-null';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {ReduxFormSelectTwoLineIcon} from 'shells/redux-form-fields/select-two-line-icon/select-two-line-icon';

import type {
    TimespanInput,
    EditionFragment as DripEdition,
    SmsDripEditionFragment as SmsDripEdition,
} from 'nutshell-graphql-types';

import * as Forms from '../../../core/form/fields';

import {EditActiveDelayConfirmation} from '../drip-sequence-page/edit-active-delay-confirmation';

import type {TriggerTypeEnum, TriggerTypeValue} from './constants';
import {FORM_NAME} from './drip-sequence-sidebar-form';
import {SetOptionalDelay} from './sidebar-forms/set-optional-delay';
import {StopOnLeadClosed} from './sidebar-forms/stop-on-lead-closed';
import {StopOnRemovedFromAudience} from './sidebar-forms/stop-on-removed-from-audience';

import styles from './forms.module.css';

const FORMS = {
    ADD_TAG: Forms.AddTagForm,
    LEAD_CREATED: Forms.LeadCreatedForm,
    LEAD_STAGE_ENTERED: Forms.LeadStageEnteredForm,
    LEAD_CLOSED: Forms.LeadClosedForm,
    ADD_TO_AUDIENCE: Forms.AudienceForm,
} as const;

const triggerSelector = formValueSelector(FORM_NAME);

// @ts-expect-error
function mapStateToProps(state): StateProps {
    return {
        triggerType: triggerSelector(state, 'triggerType'),
    };
}

// @ts-expect-error
function mapDispatchToProps(dispatch): DispatchProps {
    return {
        onTriggerTypeChange: (newValue?: TriggerTypeEnum | null) => {
            dispatch(reset(FORM_NAME));
            dispatch(change(FORM_NAME, 'triggerType', newValue));
        },
    };
}

type OwnProps = {
    isStopOnLeadClosed: boolean;
    setIsStopOnLeadClosed: (arg1: boolean) => void;
    isStopOnRemovedFromAudience: boolean;
    setIsStopOnRemovedFromAudience: (arg1: boolean) => void;
    setOptionalDelay: (arg1?: TimespanInput | null | undefined) => void | null | undefined;
    delay: TimespanInput | null | undefined;
    isLoading: boolean;
    initialDelay: TimespanInput | null | undefined;
    shouldCheckForEditDelayConfirmation: boolean;
    firstEdition: DripEdition | SmsDripEdition | null | undefined;
    triggerTypeValues: TriggerTypeValue[];
    onConfirmSaveChanges: () => void;
    onCancelChanges: () => void;
};

type StateProps = {
    triggerType?: TriggerTypeEnum;
};

type DispatchProps = {
    onTriggerTypeChange: (newValue?: TriggerTypeEnum | null | undefined) => void;
};

type Props = OwnProps & StateProps & DispatchProps;

export function AddStartTriggerFormComponent(props: Props) {
    const Component = props.triggerType ? FORMS[props.triggerType] : undefined;

    const {triggerType, onTriggerTypeChange} = props;

    React.useEffect(() => {
        onTriggerTypeChange(triggerType);
    }, [triggerType, onTriggerTypeChange]);

    return (
        <div className={styles['wrapping-container']}>
            {props.isLoading ? (
                <LoadingPage />
            ) : (
                <>
                    <div className={styles['form-description']}>
                        Set up a start trigger that will automatically begin sending this drip
                        sequence.
                    </div>
                    <Label htmlFor='triggerType'>When should this drip sequence begin?</Label>
                    <Field
                        name='triggerType'
                        component={ReduxFormSelectTwoLineIcon}
                        options={props.triggerTypeValues}
                        validate={[nonNull]}
                        placeholder='Select a start trigger'
                    />
                    {Component ? (
                        <>
                            <Component tagType='CONTACT' />
                            {props.triggerType && props.triggerType !== 'LEAD_CLOSED' ? (
                                // We hide the 'Stop sending when a related lead is closed' checkbox
                                // when LEAD_CLOSED trigger is selected
                                <StopOnLeadClosed
                                    isStopOnLeadClosed={props.isStopOnLeadClosed}
                                    setIsStopOnLeadClosed={props.setIsStopOnLeadClosed}
                                />
                            ) : undefined}
                            {props.triggerType && props.triggerType === 'ADD_TO_AUDIENCE' ? (
                                // We only show this option when the ADD_TO_AUDIENCE trigger is selected
                                <StopOnRemovedFromAudience
                                    isStopOnRemovedFromAudience={props.isStopOnRemovedFromAudience}
                                    setIsStopOnRemovedFromAudience={
                                        props.setIsStopOnRemovedFromAudience
                                    }
                                />
                            ) : undefined}
                            {/* @ts-expect-error */}
                            {props.setOptionalDelay && props.triggerType ? (
                                <SetOptionalDelay
                                    delay={props.delay}
                                    onSetOptionalDelay={props.setOptionalDelay}
                                    initialDelay={props.initialDelay}
                                />
                            ) : undefined}
                            {props.shouldCheckForEditDelayConfirmation && props.firstEdition ? (
                                <EditActiveDelayConfirmation
                                    editionId={props.firstEdition.id}
                                    editionName={props.firstEdition.name}
                                    delay={props.delay}
                                    onCancel={props.onCancelChanges}
                                    onConfirmSaveChanges={props.onConfirmSaveChanges}
                                />
                            ) : undefined}
                        </>
                    ) : undefined}
                </>
            )}
        </div>
    );
}

// @ts-expect-error
const connector = connect<Props, OwnProps, _, _, _, _>(mapStateToProps, mapDispatchToProps);

export const AddStartTriggerForm = connector(AddStartTriggerFormComponent);
